import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { PageHeader, Dropdown, Menu, Button, Modal, message } from 'antd';
import { EditOutlined, PlusOutlined, DeleteOutlined, KeyOutlined, EllipsisOutlined } from '@ant-design/icons';

import * as ToolRequest from '../../tools/ToolRequest';
import * as ToolPermission from '../../tools/ToolPermission';
import * as ToolStorage from '../../tools/ToolStorage';

import SpinFull from '../../components/SpinFull';
import SuperTable from '../../components/SuperTable';
import UserResetPasswordModal from './UserResetPasswordModal';

const { confirm } = Modal;

export default function UserViewScreen() {
  const history = useHistory();
  const userData = useSelector(state => state.user);
  const userPermissionsData = useSelector(state => state.userPermissions);

  const canListUsers = ToolPermission.hasPermission(userPermissionsData, 'ui:user', 'get');
  const canCreateUser = ToolPermission.hasPermission(userPermissionsData, 'ui:user', 'post');
  const canEditOtherUsers = ToolPermission.hasPermission(userPermissionsData, 'ui:user', 'put:xu');
  const canDeleteUser = ToolPermission.hasPermission(userPermissionsData, 'ui:user', 'delete');
  const isAllowedCrossCompany = ToolPermission.hasPermission(userPermissionsData, 'ui:user', 'get:xc');

  const storedCompanyId = ToolStorage.get('userCompanyId');

  const [loaded, setLoaded] = useState(false);
  const [tableKey, setTableKey] = useState(Math.random().toString());
  const [searchCompanyOptions, setSearchCompanyOptions] = useState([]);
  const [resetPasswordUserId, setResetPasswordUserId] = useState(null);

  const columns = [
    {
      template: 'action',
      key: 'action',
      width: 80,
      render: (value, record) => {
        const menuItems = [];

        if (canEditOtherUsers || record.id === userData.id) {
          menuItems.push(
            <Menu.Item key="edit">
              <EditOutlined />
              Edit
            </Menu.Item>
          );
        }

        if (canEditOtherUsers && record.id !== userData.id) {
          menuItems.push(
            <Menu.Item key="resetPassword">
              <KeyOutlined />
              Reset Password
            </Menu.Item>
          );
        }

        if (canDeleteUser && record.id !== userData.id) {
          menuItems.push(
            <Menu.Item key="delete">
              <DeleteOutlined />
              Delete
            </Menu.Item>
          );
        }

        if (!menuItems.length) {
          return null;
        }

        return (
          <Dropdown
            overlay={
              <Menu
                onClick={menuInfo => {
                  switch (menuInfo.key) {
                    case 'edit': {
                      history.push(`/user/edit/${record.id}`);
                      break;
                    }
                    case 'resetPassword': {
                      setResetPasswordUserId(record.id);
                      break;
                    }
                    case 'delete': {
                      onDeleteUser(record);
                      break;
                    }
                  }
                }}
              >
                {menuItems}
              </Menu>
            }
          >
            <Button
              type="default"
              shape="round"
            >
              <EllipsisOutlined />
            </Button>
          </Dropdown>
        );
      },
    },
    {
      template: 'id',
      key: 'id',
      width: 40,
      title: 'ID',
    },
    {
      key: 'name',
      title: 'Username',
    },
    {
      key: 'displayName',
      title: 'Display Name',
    },
  ];

  if (isAllowedCrossCompany) {
    columns.push({
      key: 'companyName',
      title: 'Company',
    });
  }

  columns.push(
    {
      key: 'roles',
      title: 'Roles',
      render: (value) => (value || []).join(', '),
    },
    {
      template: 'datetime',
      key: 'createdAt',
      title: 'Created',
    },
  );

  const searchColumns = isAllowedCrossCompany ? [
    {
      template: 'cascader',
      key: 'companyId',
      title: 'Company',
      options: searchCompanyOptions,
      defaultValue: storedCompanyId ? [storedCompanyId] : undefined,
    },
  ] : [];

  const searchQueryParams = values => {
    return {
      companyId: values.companyId ? values.companyId[0] : undefined,
    };
  };

  const loadDataSource = async (body) => {
    const userRes = await ToolRequest.request('GET', '/v1/user', body);

    if (!isAllowedCrossCompany) {
      return {
        data: userRes.data,
        totalCount: userRes.totalCount,
      };
    }

    const companyIds = userRes.data
      .map(user => user.companyId)
      .filter((value, index, self) => self.indexOf(value) === index);

    const companies = await Promise.all(companyIds.map(companyId => ToolRequest.request('GET', `/v1/company/${companyId}`).catch(() => null)));

    return {
      data: userRes.data.map(user => {
        const company = companies.find(companyTmp => companyTmp && companyTmp.id === user.companyId);
        return {
          ...user,
          companyName: company?.displayName || '-',
        };
      }),
      totalCount: userRes.totalCount,
    };
  };

  const reloadTable = () => {
    setTableKey(Math.random().toString());
  };

  const onDeleteUser = (user) => {
    confirm({
      title: 'Are you sure you want to delete this user?',
      content: `User: ${user.displayName}`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await ToolRequest.request('DELETE', `/v1/user/${user.id}`);
          message.success('Delete User successfully');
          reloadTable();
        } catch (err) {
          if (err?.response?.data) {
            switch (err.response.data) {
              case 'DELETE_YOURSELF_NOT_ALLOWED':
                return message.error('You cannot delete yourself');
              default:
                return message.error('Unknown errors. Please retry');
            }
          }

          message.error('Unknown error. Please retry');
        }
      },
    });
  };

  useEffect(() => {
    (async () => {
      if (isAllowedCrossCompany) {
        const companyRes = await ToolRequest.request('GET', '/v1/company', {
          count: 1000,
        });

        setSearchCompanyOptions(companyRes.data.map(company => ({
          value: company.id,
          label: company.displayName,
        })));
      }

      setLoaded(true);
    })();
  }, [isAllowedCrossCompany]);

  if (!canListUsers) {
    return <SpinFull />;
  }

  return loaded ? (
    <PageHeader
      title="User List"
      extra={canCreateUser ? [
        <Button
          key="add-user"
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => history.push('/user/add')}
        >
          Add User
        </Button>,
      ] : []}
    >
      <SuperTable
        searchColumns={searchColumns}
        searchQueryParams={searchQueryParams}
        tableKey={tableKey}
        onTableKeyChange={key => setTableKey(key)}
        onSearchChange={(values) => {
          if (!isAllowedCrossCompany) return;
          ToolStorage.set('userCompanyId', values.companyId?.[0] ?? null);
        }}
        loadDataSource={loadDataSource}
        columns={columns}
      />

      <UserResetPasswordModal
        visible={!!resetPasswordUserId}
        userId={resetPasswordUserId}
        onOk={() => setResetPasswordUserId(null)}
        onCancel={() => setResetPasswordUserId(null)}
      />
    </PageHeader>
  ) : <SpinFull />;
}
